import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLoginDialog } from "@/contexts/LoginDialogContext";
import { useSignupDialog } from "@/contexts/SignupDialogContext";
import { useUserRole } from "@/hooks/useMarketplace";
import {
  Home, ShoppingBag, MessageCircle, User, Menu, X, Plus, Search, LogOut,
  Newspaper, TrendingUp, Users, MessageSquare, Calendar, Heart,
  Video, Phone, Gamepad2, Briefcase, Wallet, Truck, Zap, Gavel,
  Hammer, Bookmark, Car, LandPlot, Sun, ArrowRightLeft, Scale, Wrench, Shield,
  Building2, Calculator, Megaphone, Settings, CreditCard, Crown, Scissors,
  ArrowUpDown, FileText, TreePine, Mail, GraduationCap, BookOpen, BatteryCharging,
  Fuel, Landmark, UtensilsCrossed, FolderKanban, HardHat, Languages, ChefHat,
  QrCode, Gift, FolderUp, ChevronDown, ChevronRight, Swords,
  Brain, Hotel, Smartphone, Lightbulb, CandlestickChart, Vote,
  Moon, LayoutDashboard, Bug, CarTaxiFront,
  Plane,
  Globe2, Upload, Sparkles,
} from "lucide-react";
import { useState, forwardRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useProfile } from "@/hooks/useProfile";
import { cn } from "@/lib/utils";
import { useIsUrlHidden } from "@/hooks/useHiddenPageKeys";
import { useSiteSetting } from "@/hooks/useSiteSettings";

// Matches FeedSidebar exactly
const navSections = [
  {
    title: "Social & Community",
    items: [
      { title: "Groups", url: "/groups", icon: Users },
      { title: "Forum", url: "/forum", icon: MessageSquare },
      { title: "Events", url: "/events", icon: Calendar },
      { title: "Chat", url: "/chat", icon: MessageCircle },
      { title: "School Zone", url: "/campus", icon: GraduationCap },
      { title: "Global Halls", url: "/hall", icon: Crown },
      { title: "Meet the Talent", url: "/talent", icon: Sparkles },
      { title: "QuizVerse", url: "/quizverse", icon: Brain },
      { title: "Bible Wall", url: "/bible-wall", icon: BookOpen },
    ],
  },
  {
    title: "Entertainment",
    items: [
      { title: "Video & LIVE", url: "/video", icon: Video },
      { title: "Video Meetings", url: "/meetings", icon: Phone },
      { title: "Dating", url: "/dating", icon: Heart },
    ],
  },
  {
    title: "Finance & Tools",
    items: [
      { title: "Wallet", url: "/wallet", icon: Wallet },
      { title: "Forex Trading", url: "/trading", icon: CandlestickChart },
      { title: "Currency Flip", url: "/currency-flip", icon: ArrowUpDown },
      { title: "Escrow", url: "/escrow", icon: Shield },
      { title: "Virtual Market", url: "/virtual-market", icon: ArrowRightLeft },
      { title: "Predictions", url: "/predictions", icon: TrendingUp },
      { title: "Receipt Ledger", url: "/accounting", icon: Calculator },
      { title: "BankConnect", url: "/bank-connect", icon: Landmark },
    ],
  },
  {
    title: "Jobs & Work",
    items: [
      { title: "Jobs", url: "/jobs", icon: Briefcase },
      { title: "Resume AI", url: "/resume-ai", icon: FileText },
      { title: "Interview Question", url: "/interview-generator", icon: Briefcase },
      { title: "Contract Bids", url: "/contract-bids", icon: Hammer },
      { title: "Job Insights", url: "/job-insights", icon: Lightbulb },
    ],
  },
  {
    title: "Marketplace",
    items: [
      { title: "Market Hub", url: "/marketplace", icon: ShoppingBag },
      { title: "Skill Trades", url: "/skill-trades", icon: ArrowRightLeft },
      { title: "Freelance", url: "/freelance", icon: Briefcase },
      { title: "Logistics", url: "/logistics", icon: Truck },
      { title: "Food Court", url: "/food-court", icon: ChefHat },
      { title: "Beauty & Spa", url: "/beauty", icon: Scissors },
      { title: "TravelHub", url: "/staysphere", icon: Plane },
      { title: "Raffles", url: "/raffles", icon: Gift },
      { title: "Legal Directory", url: "/legal-directory", icon: Scale },
      { title: "Business Directory", url: "/business-directory", icon: Building2 },
    ],
  },
  {
    title: "Services",
    items: [
      { title: "Giving Hub", url: "/giving", icon: Heart },
      { title: "QR Generator", url: "/qr-generator", icon: QrCode },
      { title: "Digital Cards", url: "/digital-cards", icon: CreditCard },
      { title: "Invitations", url: "/invitations", icon: Mail },
      { title: "File Transfer", url: "/file-sharing", icon: FolderUp },
    ],
  },
  {
    title: "Top-ups & Bills",
    items: [
      { title: "Airtime Top-up", url: "/airtime", icon: Smartphone },
      { title: "Utility Payments", url: "/utility-payments", icon: Zap },
      { title: "eSIM Store", url: "/esim-store", icon: Smartphone },
      { title: "Gift Cards", url: "/gift-cards", icon: Gift },
    ],
  },
];

const bottomNavItems = [
  { title: "Home", url: "/feed", icon: Home },
  { title: "Market", url: "/marketplace", icon: ShoppingBag },
  { title: "Wallet", url: "/wallet", icon: Wallet },
  { title: "Chat", url: "/chat", icon: MessageCircle },
];

// Special bottom nav for inner Election Tracker pages on mobile.
// Replaces Market/Wallet/Chat with tracker-specific quick links to aid
// back-and-forth navigation within the Election Tracker section.
const electionInnerBottomNavItems = [
  { title: "Home", url: "/feed", icon: Home },
  { title: "Tracker", url: "/elections", icon: Vote },
  { title: "Map", url: "/elections/map", icon: Globe2 as any },
  { title: "Upload", url: "/elections/upload", icon: Upload as any },
];

function CollapsibleMobileSection({ title, items, isActive, user, setOpen, extra, expanded, onToggle }: {
  title: string; items: { title: string; url: string; icon: any }[];
  isActive: (url: string) => boolean; user: any; setOpen: (v: boolean) => void;
  extra?: React.ReactNode;
  expanded: boolean; onToggle: () => void;
}) {
  const toggle = onToggle;
  return (
    <div className="mb-1">
      <button
        onClick={toggle}
        className="flex items-center justify-between w-full px-4 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:bg-muted/30 transition-colors"
      >
        <span>{title}</span>
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {expanded && (
        <div className="space-y-0.5">
          {items.map((item) => {
            const active = isActive(item.url);
            const url = item.url === "/profile/me" && user ? `/profile/${user.id}` : item.url;
            return (
              <Link
                key={item.title}
                to={url}
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-6 py-2 transition-colors",
                  active ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted/50"
                )}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="text-sm truncate">{item.title}</span>
              </Link>
            );
          })}
          {extra}
        </div>
      )}
    </div>
  );
}

const MobileNav = forwardRef<HTMLDivElement>((_, ref) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const { openSignupDialog } = useSignupDialog();
  const { data: registrationDisabled } = useSiteSetting("registration_disabled");
  const { data: profile } = useProfile(user?.id);
  const { data: roles } = useUserRole();
  const isAdmin = roles?.includes("admin") ?? false;
  const isUrlHidden = useIsUrlHidden();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [dark, setDark] = useState(() =>
    typeof window !== "undefined" && document.documentElement.classList.contains("dark")
  );
  const displayName = profile?.display_name || user?.email?.split("@")[0] || "User";

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const isAuthPage = location.pathname === "/" || location.pathname === "/signup" || location.pathname === "/login" || location.pathname === "/forgot-password" || location.pathname === "/reset-password";

  const isActive = (url: string) => {
    if (url === "/profile/me") return location.pathname.startsWith("/profile");
    return location.pathname === url || location.pathname.startsWith(url + "/");
  };

  if (isAuthPage) return null;

  // Detect inner Election Tracker pages (any /elections/* subroute, not /elections itself)
  const isElectionInner =
    location.pathname.startsWith("/elections/") && location.pathname !== "/elections";
  const activeBottomNavItems = isElectionInner ? electionInnerBottomNavItems : bottomNavItems;

  // Build account items
  const accountItems: { title: string; url: string; icon: any }[] = [
    { title: "My Profile", url: "/profile/me", icon: User },
    ...(isAdmin
      ? [{ title: "Admin Dashboard", url: "/admin", icon: Shield }]
      : [{ title: "Dashboard", url: "/dashboard", icon: LayoutDashboard }]
    ),
    { title: "Engage", url: "/engage", icon: Zap },
    { title: "Subscription", url: "/subscription", icon: CreditCard },
    { title: "Advertise", url: "/advertise", icon: Megaphone },
    { title: "Bug Report", url: "/bug-report", icon: Bug },
  ];

  return (
    <div ref={ref}>
      <nav className="mobile-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/98 backdrop-blur-xl border-t border-border/70 shadow-[0_-4px_16px_rgba(0,0,0,0.25)] pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch justify-around h-16 px-1">
          {activeBottomNavItems.map((item) => {
            const active = isActive(item.url);
            const url = item.url === "/profile/me" && user ? `/profile/${user.id}` : item.url;
            return (
              <Link
                key={item.title}
                to={url}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center gap-1 py-1.5 transition-colors relative",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-primary" />}
                <item.icon className={cn("w-[min(22px,100vw)] md:w-[22px] h-[min(22px,100vh)] md:h-[22px]", active && "text-primary")} strokeWidth={active ? 2.25 : 1.75} />
                <span className={cn("text-[11px] tracking-tight", active ? "font-semibold" : "font-medium")}>{item.title}</span>
              </Link>
            );
          })}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="flex-1 flex flex-col items-center justify-center gap-1 py-1.5 transition-colors text-muted-foreground hover:text-foreground">
                <Menu className="w-[min(22px,100vw)] md:w-[22px] h-[min(22px,100vh)] md:h-[22px]" strokeWidth={1.75} />
                <span className="text-[11px] font-medium tracking-tight">Menu</span>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-[min(320px,100vw)] md:max-w-[320px] p-0">
              <SheetHeader className="p-4 border-b border-border">
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>

              <ScrollArea className="h-[calc(100vh-80px)] pb-20">
                {user && (
                  <div className="p-4 border-b border-border">
                    <Link
                      to={`/profile/${user.id}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3"
                    >
                      <Avatar className="w-10 h-10">
                        {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
                        <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                          {displayName[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </Link>
                  </div>
                )}

                <div className="py-2">
                  {/* Home standalone */}
                  <Link
                    to="/feed"
                    onClick={() => setOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-6 py-2.5 transition-colors",
                      isActive("/feed") ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted/50"
                    )}
                  >
                    <Home className="w-4 h-4 shrink-0" />
                    <span className="text-sm">Home</span>
                  </Link>

                  {/* News standalone */}
                  <Link
                    to="/news"
                    onClick={() => setOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-6 py-2.5 transition-colors",
                      isActive("/news") ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted/50"
                    )}
                  >
                    <Newspaper className="w-4 h-4 shrink-0" />
                    <span className="text-sm">News</span>
                  </Link>

                  {/* Election Tracker standalone */}
                  <Link
                    to="/elections"
                    onClick={() => setOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-6 py-2.5 transition-colors",
                      isActive("/elections") ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted/50"
                    )}
                  >
                    <Vote className="w-4 h-4 shrink-0" />
                    <span className="text-sm">Election Tracker</span>
                  </Link>

                  {navSections.map((section) => {
                    // Filter out items already in the bottom nav or shown standalone above
                    const standaloneUrls = ["/news", "/elections"];
                    const filteredItems = section.items.filter(
                      (item) =>
                        !bottomNavItems.some((bn) => bn.url === item.url) &&
                        !standaloneUrls.includes(item.url) &&
                        !isUrlHidden(item.url)
                    );
                    if (filteredItems.length === 0) return null;
                    return (
                      <CollapsibleMobileSection
                        key={section.title}
                        title={section.title}
                        isActive={isActive}
                        user={user}
                        setOpen={setOpen}
                        items={filteredItems}
                        expanded={openMenu === section.title}
                        onToggle={() => setOpenMenu((m) => (m === section.title ? null : section.title))}
                      />
                    );
                  })}

                  {/* Account section */}
                  <CollapsibleMobileSection
                    title="Account"
                    items={accountItems}
                    isActive={isActive}
                    user={user}
                    setOpen={setOpen}
                    expanded={openMenu === "Account"}
                    onToggle={() => setOpenMenu((m) => (m === "Account" ? null : "Account"))}
                    extra={
                      user ? (
                        <button
                          onClick={toggleDark}
                          className="flex items-center gap-3 w-full px-6 py-2 text-foreground hover:bg-muted/50 transition-colors"
                        >
                          {dark ? <Sun className="w-4 h-4 shrink-0" /> : <Moon className="w-4 h-4 shrink-0" />}
                          <span className="text-sm truncate">{dark ? "Light Mode" : "Dark Mode"}</span>
                        </button>
                      ) : undefined
                    }
                  />
                </div>

                {user && (
                  <div className="p-4 border-t border-border">
                    <Button
                      variant="outline"
                      className="w-full gap-2 text-destructive hover:text-destructive"
                      onClick={() => { setOpen(false); signOut(); }}
                    >
                      <LogOut className="w-4 h-4" /> Log Out
                    </Button>
                  </div>
                )}

                {!user && (
                  <div className="p-4 border-t border-border space-y-2">
                    <Button className="w-full" onClick={() => { setOpen(false); openLoginDialog(); }}>
                      Log In
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => { setOpen(false); openSignupDialog(); }}
                      disabled={registrationDisabled}
                      title={registrationDisabled ? "Registration is temporarily disabled" : undefined}
                    >
                      Sign Up
                    </Button>
                    {registrationDisabled && (
                      <p className="text-xs text-center text-destructive">Registration is temporarily disabled.</p>
                    )}
                  </div>
                )}
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <style>{`
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        body.chat-thread-open .mobile-bottom-nav { display: none !important; }
      `}</style>
    </div>
  );
});

MobileNav.displayName = "MobileNav";

export default MobileNav;
